<script setup>
import {reactive, ref, onMounted} from "vue";
import router from "@/router/index.js";
import YTTablePagination from "@/components/YTTablePagination/YTTablePagination.vue";
import {listInsurance, putOnOrOffInsurance} from '@/api/insurance/insurance.js'
import {insuranceCompanyEunm} from "@/enum/insuranceCompanyEnum.js";
import {insuranceDurationUnitEnum} from "@/enum/insuranceDurationUnitEnum";

onMounted(async () => {
  await handleSearch()
})

/*搜索功能*/
// 表单数据
const formData = reactive({
  insuranceName: null,
  insuranceCompany: null,
  insuranceProductCode: null,
})
// 保险公司枚举
const insuranceCompanyOptions = ref(insuranceCompanyEunm.map(item => ({
  label: item.stateName,
  value: item.stateId
})))
// 根据表单搜索信息
const handleSearch = async (mode = 'init') => {
  if (mode === 'btnSearch') YTTablePaginationRef.value?.resetPage()
  try {
    const param = {
      page: YTTablePaginationRef.value?.getPage(),
      pageSize: YTTablePaginationRef.value?.getPageSize()
    }
    loading.value = true
    const res = await listInsurance(param, formData)
    tableData.value = res.list || []
    total.value = parseInt(res.total || 0)
    loading.value = false
  } catch (e) {
    console.log(e)
    loading.value = false
  }
}
// 清空表单内容
const handleReset = () => {
  formData.insuranceName = null
  formData.insuranceCompany = null
  formData.insuranceProductCode = null
  handleSearch('btnSearch')
}

const handleAddInsurance = () => {
  router.push({path: "/editThirdPartyInsurance"})
}

/*表格数据显示*/
// 翻页组件锚点
const YTTablePaginationRef = ref()
// 翻页信息：总条目数、加载状态
const total = ref(0)
const loading = ref(false)
const headers = ref([
  {title: '保险产品名称', key: 'insuranceName', nowrap: true},
  {title: '保险编号', key: 'insuranceProductCode', nowrap: true},
  {title: '保险公司', key: 'insuranceCompany', nowrap: true},
  {title: '保险保障期限', key: 'insuranceDuration', nowrap: true},
  {title: '价格', key: 'insurancePrice', nowrap: true},
  {title: '创建时间', key: 'crTime', nowrap: true},
  {title: '状态', key: 'insuranceState', nowrap: true},
  {title: '操作', key: 'controls', minWidth: '120px', fixed: true},
])
// 表格数据
const tableData = ref([])
// 翻页
const handleChangePage = () => {
  handleSearch()
}

/*操作栏功能*/
const getInsuranceId = (item) => item?.insuranceId || item?.id
// 详情
const handleDetail = (item) => {
  router.push({path: "/editThirdPartyInsurance", query: {insuranceId: getInsuranceId(item)}})
}
// 上架/下架
const handleChangeInsuranceState = async (item) => {
  const nextState = typeof item?.insuranceState === 'number'
      ? (isInsuranceOnShelf(item) ? 0 : 1)
      : (isInsuranceOnShelf(item) ? 'OFF_SHELF' : 'ON_SHELF')
  try {
    await putOnOrOffInsurance(getInsuranceId(item), nextState)
    ElMessage.success(`${isInsuranceOnShelf(item) ? '下架' : '上架'}成功`)
    await handleSearch()
  } catch (e) {
    console.log(e)
  }
}
// 状态展示
const handleStateText = (state) => {
  if (state === 'ON_SHELF' || state === 1) return '上架'
  if (state === 'OFF_SHELF' || state === 0) return '下架'
  return '-'
}
// 状态类型展示
const handleStateType = (state) => {
  if (state === 'ON_SHELF' || state === 1) return 'success'
  if (state === 'OFF_SHELF' || state === 0) return 'error'
  return 'info'
}
// 保险保障期限展示
const handleInsuranceDurationText = (item) => {
  if (!item?.insuranceDuration) return '-'
  const unit = insuranceDurationUnitEnum.find(unitItem => unitItem.stateId === item.insuranceDurationUnit)
  return `${item.insuranceDuration}${unit?.stateName || ''}`
}
// 是否已上架
const isInsuranceOnShelf = (item) => {
  return item?.insuranceState === 'ON_SHELF' || item?.insuranceState === 1
}
</script>

<template>
  <div class="container_box mg_b_2 flex_left" style="padding: 16px;">
    <v-btn color="success" @click="handleAddInsurance">新增保险</v-btn>
  </div>

  <div class="container_box mg_b_2" style="padding: 16px;">
    <v-form>
      <v-container>
        <v-row justify="start">
          <v-col cols="3">
            <v-text-field
                v-model="formData.insuranceName"
                placeholder="请输入保险产品名称"
                label="保险产品名称"
                variant="outlined"
                clearable
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-select
                v-model="formData.insuranceCompany"
                placeholder="请选择保险公司"
                label="保险公司"
                variant="outlined"
                clearable
                :items="insuranceCompanyOptions"
                item-title="label"
                item-value="value"
            ></v-select>
          </v-col>
          <v-col cols="3">
            <v-text-field
                v-model="formData.insuranceProductCode"
                placeholder="请输入保险编号"
                label="保险编号"
                variant="outlined"
                clearable
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-row justify="end">
              <v-col class="flex_right" cols="6">
                <v-btn color="success" size="large" @click="handleSearch('btnSearch')">搜索</v-btn>
              </v-col>
              <v-col class="flex_right" cols="6">
                <v-btn size="large" @click="handleReset">重置</v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </div>

  <div class="container_box">
    <v-row justify="center">
      <v-col cols="11">
        <v-data-table
            :headers="headers"
            :items="tableData"
            :loading="loading"
            :items-per-page="$globalPageSize"
            :no-data-text="'没有更多数据了'"
            hover
        >
          <template v-slot:loading>
            <v-skeleton-loader type="table-row@1"></v-skeleton-loader>
          </template>

          <template v-slot:item.insuranceDuration="{ item }">
            {{ handleInsuranceDurationText(item) }}
          </template>

          <template v-slot:item.insuranceState="{ item }">
            <el-text :type="handleStateType(item.insuranceState)">
              {{ handleStateText(item.insuranceState) }}
            </el-text>
          </template>

          <template v-slot:item.controls="{ item }">
            <div class="flex_left">
              <el-tooltip
                  class="box-item"
                  effect="dark"
                  content="详情"
                  placement="left">
                <v-icon class="mg_r_1" icon="mdi-file-document-outline" color="primary" size="small" @click="handleDetail(item)"></v-icon>
              </el-tooltip>
              <el-popconfirm @confirm="handleChangeInsuranceState(item)" :title="`确认${isInsuranceOnShelf(item) ? '下架' : '上架'}该保险产品？`">
                <template #reference>
                  <div>
                    <el-tooltip
                        class="box-item"
                        effect="dark"
                        :content="isInsuranceOnShelf(item) ? '下架' : '上架'"
                        placement="right">
                      <!-- <el-text class="mx-1 cursor-pointer" type="primary">
                        {{ isInsuranceOnShelf(item) ? '下架' : '上架' }}
                      </el-text> -->
                      <v-icon class="cursor-pointer" :color="isInsuranceOnShelf(item) ? 'error' : 'success'" size="small">{{ isInsuranceOnShelf(item) ? 'mdi-tray-arrow-down' : 'mdi-tray-arrow-up' }}</v-icon>
                    </el-tooltip>
                  </div>
                </template>
              </el-popconfirm>
            </div>
          </template>

          <template v-slot:bottom>
            <YTTablePagination ref="YTTablePaginationRef" :total="total" @page-change="handleChangePage"></YTTablePagination>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>

</style>
